import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import SelectCountry from "react-select";
import { currentSelectedLanguage } from "../../../../../StateManagement/actions";
import { contactsService } from "../../../../../Services/contactsService";

const globePlaceholder = {
  label: "+Code",
  value: "+Code",
  languageCode: "+Code",
  isPlaceholder: true
};

export function ContactModal({ isOpen, onClose, onSave, initialContact, existingContacts = [], contacts = [] }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneCode, setPhoneCode] = useState("+1");
  const [mobile, setMobile] = useState("");
  const [referenceTag, setReferenceTag] = useState("");
  const [errors, setErrors] = useState({});
  const [countryCodeOptions, setCountryCodeOptions] = useState([]);

  useEffect(() => {
    if (initialContact) {
      setEmail(initialContact.email || "");
      setFirstName(initialContact.firstName || "");
      setLastName(initialContact.lastName || "");
      let rawCode = initialContact.phoneCode || "+1";
      if (rawCode.includes("-")) {
        rawCode = rawCode.split("-")[0];
      }
      setPhoneCode(rawCode);
      setMobile(initialContact.mobile || "");
      setReferenceTag(
        initialContact.referenceTag || initialContact.reference || ""
      );
    } else {
      setEmail("");
      setFirstName("");
      setLastName("");
      setPhoneCode("+1");
      setMobile("");
      setReferenceTag("");
    }
    setErrors({});
  }, [initialContact, isOpen]);

  // Fetch country codes matching EditReaderModal & Level3AddUserModal
  const fetchCountryCodes = useCallback(async () => {
    try {
      const data = await contactsService.getCountryCodes();
      if (data && Array.isArray(data) && data.length > 0) {
        const formattedOptions = [
          {
            value: "+Code",
            label: "+Code",
            languageCode: "+Code",
            mobileMaxLeng: 0
          },
          ...data.map((item) => ({
            value: `${item.dialCode}-${item.countryCode}`,
            label: `${item.countryName} (${item.dialCode})`,
            languageCode: item.countryCode ? item.countryCode.toLowerCase() : "",
            mobileMaxLeng: item.mobileMaxLeng
          }))
        ];
        setCountryCodeOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCountryCodes();
    }
  }, [isOpen, fetchCountryCodes]);

  const selectedCountryObj = useMemo(() => {
    if (!phoneCode || phoneCode === "+Code") {
      return globePlaceholder;
    }
    const dailCode = phoneCode.includes("-") ? phoneCode.split("-")[0] : phoneCode;
    return (
      countryCodeOptions.find((opt) => {
        if (opt.value === "+Code") return false;
        const optDialCode = opt.value.includes("-") ? opt.value.split("-")[0] : opt.value;
        return optDialCode === dailCode || opt.value === phoneCode;
      }) || globePlaceholder
    );
  }, [phoneCode, countryCodeOptions]);

  const handleCodeChange = (selectedOption) => {
    if (!selectedOption || selectedOption.value === "+Code") {
      setPhoneCode("+Code");
      return;
    }
    const dailCode = selectedOption.value.includes("-") ? selectedOption.value.split("-")[0] : selectedOption.value;
    setPhoneCode(dailCode);
  };

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedReference = referenceTag.trim();

    if (!trimmedEmail) {
      newErrors.email = selectedLanguage?.LstLanguageCodes?.UI_EmailAdressIsRequired;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        newErrors.email = selectedLanguage?.LstLanguageCodes?.UI_Pleaseentervalidmail;
      } else {
        const checkList = existingContacts.length > 0 ? existingContacts : contacts;
        const isDuplicate = (checkList || []).some((c) => {
          if (initialContact && (c.id === initialContact.id || c.email?.toLowerCase() === initialContact.email?.toLowerCase())) {
            return false;
          }
          return (c.email || "").trim().toLowerCase() === trimmedEmail;
        });
        if (isDuplicate) {
          newErrors.email = selectedLanguage?.LstLanguageCodes?.UI_DuplicateEmailAdress;
        }
      }
    }

    if (trimmedFirstName.length > 50) {
      newErrors.firstName = selectedLanguage?.LstLanguageCodes?.UI_FirstNameMoreThanFifty;
    }

    if (trimmedLastName.length > 50) {
      newErrors.lastName = selectedLanguage?.LstLanguageCodes?.UI_LastNameMoreThanFifty;
    }

    if (trimmedReference.length > 50) {
      newErrors.referenceTag = selectedLanguage?.LstLanguageCodes?.UI_ReferenceMoreThanFifty;
    }

    if (mobile.trim()) {
      const cleanNum = mobile.replace(/[^0-9]/g, "");
      if (cleanNum.length < 5 || cleanNum.length > 15) {
        newErrors.mobile = selectedLanguage?.LstLanguageCodes?.UI_PleaseEnterValidNumber;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    let finalPhoneCode = phoneCode;
    if (finalPhoneCode.includes("-")) {
      finalPhoneCode = finalPhoneCode.split("-")[0];
    }

    const contactData = {
      ...(initialContact ? { id: initialContact.id } : {}),
      email: email.trim().toLowerCase(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneCode: finalPhoneCode,
      mobile: mobile.trim(),
      referenceTag: referenceTag.trim(),
      scope: initialContact?.scope || undefined
    };

    onSave(contactData);
  };

  return (
    <div className="modalCus contacts-modal-show" id="myModal">
      <div className="modal-dialog modal-dialog-centered custom-width-model" style={{ maxWidth: "440px" }}      >
        <div className="modal-content">
          {/* Header matching EditReaderModal.js */}
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0 text-danger fw-bold font-20 clsFnLnHeading">
              {initialContact ? selectedLanguage?.LstLanguageCodes?.UI_EditContact : selectedLanguage?.LstLanguageCodes?.UI_AddNewContact}
            </h4>
            <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0" onClick={onClose}>
              <span className="material-icons font-20">highlight_off</span>
            </button>
          </div>

          {/* Form Body matching EditReaderModal.js */}
          <form onSubmit={handleSubmit} noValidate>
            <div className="modal-body ps-1 pt-0 pe-3">
              {/* Email Address Field */}
              <div className="col-sm-12 col-md-12 res-pe0 mt-3 ps-3 readerEdit-mail">
                {!initialContact ? (<input disabled={initialContact} type="email" className={`clsFnLn w-100 form-control ${errors.email ? "is-invalid border-danger" : ""}`} name="email" title={selectedLanguage?.LstLanguageCodes?.UI_EmailAdress}
                  placeholder={selectedLanguage?.LstLanguageCodes?.UI_EmailAdress} value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                  }} autoFocus
                />) : (<label className="wordwrap mail-text-contact text-wrap">{email}</label>)}
                {errors.email && <div className="text-danger font-12 mt-1 ms-1">{errors.email}</div>}
              </div>

              {/* First Name Field */}
              <div className="col-sm-12 col-md-12 res-pe0 mt-3 ps-3 readerEdit-mail">
                <input type="text" className={`clsFnLn w-100 form-control ${errors.firstName ? "is-invalid border-danger" : ""}`} name="FirstName"
                  title={selectedLanguage?.LstLanguageCodes?.UI_FirstName} placeholder={selectedLanguage?.LstLanguageCodes?.UI_FirstName} value={firstName} maxLength={50}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
                  }}
                />
                {errors.firstName && <div className="text-danger font-12 mt-1 ms-1">{errors.firstName}</div>}
              </div>

              {/* Last Name Field */}
              <div className="col-sm-12 col-md-12 res-pe0 mt-3 ps-3 readerEdit-mail">
                <input type="text" className={`clsFnLn w-100 form-control ${errors.lastName ? "is-invalid border-danger" : ""}`} name="LastName"
                  title={selectedLanguage?.LstLanguageCodes?.UI_LastName} placeholder={selectedLanguage?.LstLanguageCodes?.UI_LastName} value={lastName} maxLength={50}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
                  }}
                />
                {errors.lastName && <div className="text-danger font-12 mt-1 ms-1">{errors.lastName}</div>}
              </div>

              {/* Country Code & Mobile Number Input Group */}
              <div className="col-sm-12 col-md-12 col-lg-12 mt-3 ps-3">
                <div className="input-group custom-inputBorder">
                  <span className="input-group-text mobile-group50" style={{ padding: "0px", width: "36%" }}>
                    <SelectCountry value={selectedCountryObj?.value ? selectedCountryObj : globePlaceholder} onChange={(event) => handleCodeChange(event)} options={countryCodeOptions}
                      menuPosition="fixed" isSearchable={false} styles={{
                        control: (provided) => ({
                          ...provided, display: "flex", flexWrap: "nowrap", alignItems: "center", justifyContent: "space-between", fontSize: "14px",
                          width: "100%", minHeight: "40px", height: "40px", backgroundColor: "transparent", border: "none", boxShadow: "none", cursor: "pointer"
                        }),
                        valueContainer: (provided) => ({ ...provided, display: "flex", alignItems: "center", flexWrap: "nowrap", height: "40px", padding: "0 0 0 8px", overflow: "hidden" }),
                        singleValue: (provided) => ({
                          ...provided, display: "flex", alignItems: "center", margin: 0, maxWidth: "100%", position: "relative",
                          transform: "none", top: "auto", color: "#272d4e", fontSize: "14px", fontWeight: "500"
                        }),
                        indicatorsContainer: (provided) => ({ ...provided, display: "flex", alignItems: "center", height: "40px", paddingRight: "2px" }),
                        dropdownIndicator: (provided) => ({ ...provided, padding: "0 4px", color: "#333" }),
                        placeholder: (provided) => ({ ...provided, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "center", width: "100%", cursor: "default" }),
                        menu: (provided) => ({ ...provided, zIndex: 9999, width: "233px", overflow: "hidden", maxHeight: "300px", overflowY: "auto" }),
                        menuPortal: (provided) => ({ ...provided, zIndex: 9999, width: "233px" }),
                        indicatorSeparator: () => ({ display: "none" })
                      }}
                      menuPortalTarget={document.body} getOptionLabel={(e) => `${e.label}`} getOptionValue={(e) => `${e.value}`} title="Select code"
                      formatOptionLabel={(data, { context }) => (
                        <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                          <div className={`${!data?.languageCode || data?.languageCode === "+Code" || data?.label === "+Code" ? "bg-" : `bg-${data?.languageCode}`}`}
                            style={{ width: "16px", height: "16px", minWidth: "16px", marginRight: "6px", flexShrink: 0 }}>
                          </div>
                          <span style={{ fontSize: "14px", color: "#272d4e", lineHeight: "1" }}>
                            {context === "menu" ? `${data?.label}` : `${data?.value?.split("-")[0]}`}
                          </span>
                        </div>
                      )}
                      noOptionsMessage={() => null}
                    />
                  </span>
                  <input type="text" className="form-control res-mobileinput" value={mobile} placeholder={selectedLanguage?.LstLanguageCodes?.UI_Mobile}
                    maxLength={!selectedCountryObj || selectedCountryObj.value === "+Code" ? 10 : selectedCountryObj.mobileMaxLeng || 15}
                    onChange={(e) => {
                      setMobile(e.target.value.replace(/[^0-9]/g, ""));
                      if (errors.mobile) {
                        setErrors((prev) => ({ ...prev, mobile: undefined }));
                      }
                    }}
                  />
                </div>
                {errors.mobile && (<div className="text-danger font-12 mt-1 ms-1">{errors.mobile}</div>)}
              </div>

              {/* Reference Tag Field */}
              <div className="col-sm-12 col-md-12 res-pe0 mt-3 ps-3 readerEdit-mail">
                <input type="text" className={`clsFnLn w-100 form-control ${errors.referenceTag ? "is-invalid border-danger" : ""}`} name="reference" title={selectedLanguage?.LstLanguageCodes?.UI_ReferenceTxt}
                  placeholder={selectedLanguage?.LstLanguageCodes?.UI_ReferenceTxt} value={referenceTag} maxLength={50}
                  onChange={(e) => {
                    setReferenceTag(e.target.value);
                    if (errors.referenceTag) setErrors((prev) => ({ ...prev, referenceTag: undefined }));
                  }}
                />
                {errors.referenceTag && <div className="text-danger font-12 mt-1 ms-1">{errors.referenceTag}</div>}
              </div>
            </div>

            {/* Modal Footer matching RDocs Modal standards */}
            <div className="modal-footer d-flex align-items-center justify-content-end gap-2">
              <button type="button" className="btn btn-danger px-4 w-140p truncate" onClick={onClose}              >
                {selectedLanguage?.LstLanguageCodes?.UI_Cancel}
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4 w-140p truncate"
              >
                {initialContact ? selectedLanguage?.LstLanguageCodes?.UI_Update : selectedLanguage?.LstLanguageCodes?.UI_Save}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
export default ContactModal;